import React, { useState, useEffect, Fragment } from "react";
import { Box, Button, Typography, Grid, MenuItem, Stack } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import MpgInput from "../../../../../CommonFormComp/MpgInput";
import MpgItem from "../../../../../CommonFormComp/MpgItem";
import MpgFileUpload from "../../../../../fileHandling/MpgFileUpload";
import { createProgrammeDescriptorValidation } from "./createProgrammeDescriptorValidation";
import { managePDViewPath } from "../../../../../routes/routePaths";
import MpgErrorDialog from "../../../../../common/dialog/MpgErrorDialog";
import MpgLoadingDialog from "../../../../../common/dialog/MpgLoadingDialog";
import { useGetLoggedInUserDetails } from "../../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useCreateProgramDescriptorMutation, useUploadProgramDescriptorFileMutation, useViewProgrammeDescriptorListMutation } from "../../../../../reactQueries/AppApi/rqHooksForProgrammeDescriptor";
import useTrackUserActivity from "../../../../../userActivityTracking/useTrackUserActivity";
import FormHelperText from '@mui/material/FormHelperText';

const CreateProgrammeDescriptor = () => {
  const navigate = useNavigate();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const { emailAddress, clientPortalAdminRoleId, isEnterpriseAdmin, id: userId } = loggedInUser;

  /** !!!!!!!! form handling  !!!!!!!! */

  const { schema } = createProgrammeDescriptorValidation();

  const rhfFormInstance = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      pdFile: []
    }
  });

  const { watch, setValue, trigger } = rhfFormInstance;

  const inputPDTitle = watch("pdTitle")

  const resourcesCMSData = useOutletContext();

  const fetchMngResoRepoDtlsLabel = (key) => {
    if (resourcesCMSData && resourcesCMSData?.pageContent["ManageResourceRepoDetails"]) {
      const resourcesCMSDataLabel = resourcesCMSData.pageContent["ManageResourceRepoDetails"];
      return returnLabel(resourcesCMSDataLabel?.Labels, key)
    }
  }  

  const fetchMngResoRepoListLabel = (key) => {
    if (resourcesCMSData && resourcesCMSData?.pageContent["ManageResourceRepoList"]) {
      const resourcesCMSDataLabel = resourcesCMSData.pageContent["ManageResourceRepoList"];
      return returnLabel(resourcesCMSDataLabel?.Labels, key)
    }
  }

  const returnLabel = (labelArray, key) => {
    if (labelArray.length > 0 && key) {
        return labelArray.find((x) => x.Key === key)?.Phrase || '';
    }
    return '';
  }

  /** !!!!!!!! API calls  !!!!!!!! */
  const { mutate: getPDList, isSuccess, error, data: PDData, isError, isLoading: isLoadingOnPDData, } = useViewProgrammeDescriptorListMutation();

  useEffect(() => {
    getPDList({ loggedInUserId: userId, roleId: clientPortalAdminRoleId, isEnterpriseAdmin })
  }, [loggedInUser])

  let filteredPDListForStandardType = !!PDData?.data && PDData?.data.filter((eachItem) => eachItem?.ProgramDescripitorType === "Standard")

  // api - create report resource mutation
  const {
    mutate: createProgramDescriptor,
    isError: isErrorOnCreate,
    isLoading: isLoadingOnCreate,
    isSuccess: isSuccessOnCreate,
    data: dataOnCreate,
    error: errorOnCreate,
  } = useCreateProgramDescriptorMutation();

  const {
    mutate: uploadPDFile,
    isError: isErrorOnPDFileUpload,
    isLoading: isLoadingOnPDFileUpload,
    isSuccess: isSuccessOnPDFileUpload,
    data: dataOnPDFileUpload,
    error: errorOnPDFileUpload,
  } = useUploadProgramDescriptorFileMutation();

  /** !!!!!!!! handling submit function  !!!!!!!! */
  const [bodyDataForCreate, setBodyDataForCreate] = useState({});
  const onSubmit = (data) => {
    const body = {
      ProgramDescripitorTitle: data?.pdTitle || "",
      IsActive: true,
      CreatedBy: emailAddress || "",
      ProgramDescripitorType: "Standard",
      ProgramDescripitorURL: "",
      ProgramDescripitorFileName: "",
      ProgramDescripitorFileGuid: "",
      ProgramDescripitorFileSize: "",
    };
    setBodyDataForCreate(body);

    if (!!data.pdFile[0]) {
      const file = data.pdFile[0];

      const bodyData = {
        ...body,
        ProgramDescripitorFileName: file.name || "",
        ProgramDescripitorFileSize: file.size || "",
      };
      setBodyDataForCreate(bodyData);

      uploadPDFile({ file });
    } else {
      createProgramDescriptor({ body });
    }
  };

  useEffect(() => {
    if (!!dataOnPDFileUpload?.data?.IsSuccess) {
      const storedFileURL = dataOnPDFileUpload.data.ProgramDescripitorURL;
      const fileGuid = dataOnPDFileUpload.data.Id;
      /**merging file URL to body */
      const bodyData = {
        ...bodyDataForCreate,
        ProgramDescripitorURL: storedFileURL,
        ProgramDescripitorFileGuid: fileGuid
      };
      createProgramDescriptor({ body: bodyData });
    }
  }, [dataOnPDFileUpload?.data?.IsSuccess]);

  useEffect(() => {
    if (!!isSuccessOnCreate) {
      navigate(managePDViewPath);
    }
  }, [isSuccessOnCreate]);

  /**only Standard PD title validation  */
  let arrayOfOnlyStandardPDTitles = []
  !!filteredPDListForStandardType && filteredPDListForStandardType.forEach((eachItem) => {
    arrayOfOnlyStandardPDTitles.push(eachItem?.ProgramDescripitorTitle)
  })
  const [standardPDTitleFieldErrorMsg, setStandardPDTitleFieldErrorMsg] = useState("")
  useEffect(() => {
    if (!!arrayOfOnlyStandardPDTitles.includes(inputPDTitle?.trim())) {
      const msg = fetchMngResoRepoDtlsLabel("ManageResourceRepoDetails.ErrorMsgStandardResourceExist") //"This standard resource title already exists in records."
      setStandardPDTitleFieldErrorMsg(msg)
    }
    else {
      setStandardPDTitleFieldErrorMsg("")
    }
  }, [arrayOfOnlyStandardPDTitles, inputPDTitle])

  /**tracking user Activity */
  const { trackActivity } = useTrackUserActivity()
  useEffect(() => {
    if (!!isSuccessOnPDFileUpload) {
      trackActivity({
        activitySuccess: !!isSuccessOnPDFileUpload,
        actionType: "Uploaded",
        itemId: dataOnPDFileUpload.data.Id || "",
      })
    }
  }, [isSuccessOnPDFileUpload])

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {/* Upload New Standard Resource */}
        {resourcesCMSData?.pageContent["ManageResourceRepoDetails"].Title}
      </Typography>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        m={1}
      >
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={managePDViewPath}
        >
          {/* Resources */}
          {fetchMngResoRepoDtlsLabel("ManageResourceRepoDetails.Resources")}
        </Button>
      </Stack>


      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={rhfFormInstance.handleSubmit(onSubmit)}
      >

        <MpgItem>
          <MpgInput
            rhfFormInstance={rhfFormInstance}
            name={"pdTitle"}
            label={fetchMngResoRepoDtlsLabel("ManageResourceRepoDetails.StandardResourceTitle")} //{"Standard Resource Title"}
            fullWidth
          />
          {
            <FormHelperText error={!!standardPDTitleFieldErrorMsg}>
              {standardPDTitleFieldErrorMsg}
            </FormHelperText>
          }

          <MpgFileUpload
            name="pdFile"
            label={fetchMngResoRepoDtlsLabel("ManageResourceRepoDetails.UploadFile")} //"Upload file"
            rhfFormInstance={rhfFormInstance}
            persistantMsg={fetchMngResoRepoDtlsLabel("ManageResourceRepoDetails.AllowedFile")} //{`Only pdf/ppt file type is allowed`}
          />
        </MpgItem>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent={"space-evenly"}
          mt={3}
        >
          <Button
            variant="outlined"
            type="submit"
            size="small"
            disabled={!!standardPDTitleFieldErrorMsg}
          >
            {/* Create Standard Resource */}
            {fetchMngResoRepoDtlsLabel("ManageResourceRepoDetails.CreateStandardResource")}
          </Button>
        </Stack>
      </Box>


      {/** error dialog on file upload action */}
      {
        !!isErrorOnPDFileUpload && errorOnPDFileUpload?.response
          ?.data?.ErrorCode === "104" ? (
          < MpgErrorDialog
            open={!!isErrorOnPDFileUpload}
            title={errorOnPDFileUpload?.response
              ?.data?.ErrorCode === "104" && fetchMngResoRepoDtlsLabel("ManageResourceRepoDetails.ErrorMsgFileScanFailed")} //"Opps, file scanning failed...!"
          />
        ) : (
          < MpgErrorDialog
            open={!!isErrorOnPDFileUpload}
          />
        )
      }
      {/** error dialog on create action */}
      {
        !!!!isErrorOnCreate && errorOnCreate?.response?.data === "ProgrammeDescriptorTitleExists" ? (
          <MpgErrorDialog
            open={!!isErrorOnCreate}
            title={fetchMngResoRepoDtlsLabel('ManageResourceRepoDetails.ErrorMsgDuplicateResourceTitle')} //"Resource title exists. Try again...!"

            content={`${fetchMngResoRepoListLabel("ManageResourceRepoList.ResourceTitle")} ${watch('pdTitle')} ${fetchMngResoRepoDtlsLabel('ManageResourceRepoDetails.ErrorMsgResourceExist')} `} //already exists in our records. Please, try again with different title.
          />
        ) : (
          <MpgErrorDialog
            open={!!isErrorOnCreate}
          />
        )
      }
      <MpgLoadingDialog
        open={
          !!isLoadingOnCreate ||
          !!isLoadingOnPDFileUpload
        }
      />
    </Box>
  );
};

export default CreateProgrammeDescriptor;
